import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import {
  createUnicornRecoveryOutcomeProperties,
  normalizeUnicornRoute,
  parseUnicornPendingRecovery,
  UNICORN_RECOVERY_PENDING_KEY,
  UNICORN_RECOVERY_PENDING_TTL_MS,
  UnicornPendingRecovery,
  UnicornRecoveryOutcome,
} from 'src/app/core/domain/unicorn';
import { AppInsightsService } from './app-insights.service';

@Injectable({ providedIn: 'root' })
export class UnicornRecoveryOutcomeService {
  private readonly document = inject(DOCUMENT);
  private readonly appInsights = inject(AppInsightsService);
  private readonly resolvedIncidents = new Set<string>();

  recordNavigation(url: string): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    const pending = parseUnicornPendingRecovery(
      this.readPendingValue(storage)
    );
    if (!pending || this.resolvedIncidents.has(pending.incidentCode)) {
      return;
    }

    const now = this.getNow();
    const destination = normalizeUnicornRoute(url);
    if (
      destination === '/callback' &&
      now - pending.startedAt < UNICORN_RECOVERY_PENDING_TTL_MS
    ) {
      return;
    }
    this.resolvedIncidents.add(pending.incidentCode);
    const outcome: UnicornRecoveryOutcome =
      now - pending.startedAt >= UNICORN_RECOVERY_PENDING_TTL_MS
        ? 'expired'
        : this.isFailureDestination(destination)
          ? 'failure'
          : 'success';
    const resolved: UnicornPendingRecovery = {
      ...pending,
      destination,
    };

    let persistedOutcome: UnicornRecoveryOutcome = outcome;
    try {
      storage.removeItem(UNICORN_RECOVERY_PENDING_KEY);
      if (storage.getItem(UNICORN_RECOVERY_PENDING_KEY) !== null) {
        persistedOutcome = 'unmeasurable';
      }
    } catch {
      persistedOutcome = 'unmeasurable';
    }

    try {
      this.appInsights.trackEvent(
        'unicorn_recovery_outcome',
        createUnicornRecoveryOutcomeProperties(
          resolved,
          persistedOutcome,
          now,
        )
      );
      this.appInsights.flush();
    } catch {
      // Normal navigation must not fail because recovery telemetry is unavailable.
    }
  }

  private getStorage(): Storage | undefined {
    try {
      return this.document.defaultView?.sessionStorage;
    } catch {
      return undefined;
    }
  }

  private readPendingValue(storage: Storage): string | null {
    try {
      return storage.getItem(UNICORN_RECOVERY_PENDING_KEY);
    } catch {
      return null;
    }
  }

  private getNow(): number {
    try {
      return Date.now();
    } catch {
      return 0;
    }
  }

  private isFailureDestination(destination: string): boolean {
    return destination === '/global-error' ||
      destination === '/login' ||
      destination === '/verify-email' ||
      destination.startsWith('/page-error-') ||
      destination === '/page-not-found-404';
  }
}
